import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DashboardLayout } from '../../Components/Layout/DashboardLayout';
import { toast } from 'react-toastify';
import {
  useGetLandmarksQuery,
  useCreateLandmarkMutation,
  useUpdateLandmarkMutation,
  useDeleteLandmarkMutation
} from '../../Redux/Apis/Navigation';
import CustomButton from '../../Components/CustomButton';
import './style.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const CATEGORIES = [
  { value: 'amenity', label: 'Amenity', icon: '🏢' },
  { value: 'gate', label: 'Gate', icon: '🚪' },
  { value: 'parking', label: 'Parking', icon: '🅿️' },
  { value: 'emergency', label: 'Emergency', icon: '🚨' },
  { value: 'recreation', label: 'Recreation', icon: '⚽' },
  { value: 'dining', label: 'Dining', icon: '🍽️' },
  { value: 'shopping', label: 'Shopping', icon: '🛒' },
  { value: 'service', label: 'Service', icon: '🔧' },
  { value: 'landmark', label: 'Landmark', icon: '📍' },
  { value: 'intersection', label: 'Intersection', icon: '🛣️' },
  { value: 'other', label: 'Other', icon: '📌' }
];

const LandmarkManager = () => {
  const { data: landmarksData, refetch: refetchLandmarks } = useGetLandmarksQuery({});
  const [createLandmark] = useCreateLandmarkMutation();
  const [updateLandmark] = useUpdateLandmarkMutation();
  const [deleteLandmark] = useDeleteLandmarkMutation();

  const [placingMode, setPlacingMode] = useState(false);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [selectedLandmark, setSelectedLandmark] = useState(null);
  const [showLandmarkForm, setShowLandmarkForm] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');

  // Form state
  const [landmarkForm, setLandmarkForm] = useState({
    name: '',
    description: '',
    category: 'amenity',
    address: '',
    icon: 'map-marker',
    iconColor: '#193631',
    isNavigable: true,
    isFeatured: false,
    phone: '',
    website: '',
    hours: '',
    status: 'active'
  });

  const landmarks = landmarksData?.data?.landmarks || [];
  const filteredLandmarks = filterCategory === 'all' 
    ? landmarks 
    : landmarks.filter(l => l.category === filterCategory);

  const defaultCenter = [31.9513, -81.0585];

  const handleMapClick = (e) => {
    if (placingMode) {
      setMarkerPosition([e.latlng.lat, e.latlng.lng]);
      setShowLandmarkForm(true);
      setPlacingMode(false);
    }
  };

  const MapClickHandler = () => {
    useMapEvents({
      click: handleMapClick
    });
    return null;
  };

  const startPlacing = () => {
    setPlacingMode(true);
    setMarkerPosition(null);
    setSelectedLandmark(null);
    setShowLandmarkForm(false);
    resetForm();
    toast.info('Click on the map to place a landmark');
  };

  const cancelPlacing = () => {
    setPlacingMode(false);
    setMarkerPosition(null);
    setShowLandmarkForm(false);
    setSelectedLandmark(null);
    resetForm();
  };

  const saveLandmark = async () => {
    if (!landmarkForm.name) {
      toast.error('Please enter a landmark name');
      return;
    }

    if (!markerPosition) {
      toast.error('Please select a location on the map');
      return;
    }

    const landmarkData = {
      ...landmarkForm,
      location: {
        type: 'Point',
        coordinates: [markerPosition[1], markerPosition[0]] // [lng, lat]
      }
    };

    try {
      if (selectedLandmark) {
        await updateLandmark({ id: selectedLandmark._id, ...landmarkData }).unwrap();
        toast.success('Landmark updated successfully');
      } else {
        await createLandmark(landmarkData).unwrap();
        toast.success('Landmark created successfully');
      }
      
      refetchLandmarks();
      cancelPlacing();
    } catch (err) {
      toast.error('Failed to save landmark');
    }
  };

  const resetForm = () => {
    setLandmarkForm({
      name: '',
      description: '',
      category: 'amenity',
      address: '',
      icon: 'map-marker',
      iconColor: '#193631',
      isNavigable: true,
      isFeatured: false,
      phone: '',
      website: '',
      hours: '',
      status: 'active'
    });
  };

  const editLandmark = (landmark) => {
    setSelectedLandmark(landmark);
    setMarkerPosition([landmark.location.coordinates[1], landmark.location.coordinates[0]]);
    setLandmarkForm({
      name: landmark.name,
      description: landmark.description || '',
      category: landmark.category,
      address: landmark.address || '',
      icon: landmark.icon,
      iconColor: landmark.iconColor,
      isNavigable: landmark.isNavigable,
      isFeatured: landmark.isFeatured,
      phone: landmark.phone || '',
      website: landmark.website || '',
      hours: landmark.hours || '',
      status: landmark.status
    });
    setShowLandmarkForm(true);
  };

  const handleDeleteLandmark = async (landmarkId) => {
    if (window.confirm('Are you sure you want to delete this landmark?')) {
      try {
        await deleteLandmark(landmarkId).unwrap();
        toast.success('Landmark deleted successfully');
        refetchLandmarks();
        if (selectedLandmark?._id === landmarkId) {
          cancelPlacing();
        }
      } catch (err) {
        toast.error('Failed to delete landmark');
      }
    }
  };

  const getCategoryIcon = (category) => {
    return CATEGORIES.find(c => c.value === category)?.icon || '📍';
  };

  return (
    <DashboardLayout>
      <div className="landmarkManagerContainer">
        <div className="landmarkManagerHeader">
          <h2>Landmark Manager</h2>
          
          <div className="filterSection">
            <label>Filter by category:</label>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="all">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.icon} {cat.label}</option>
              ))}
            </select>
          </div>

          <div className="headerActions">
            {!placingMode && !showLandmarkForm && (
              <CustomButton
                text="Add New Landmark"
                onClick={startPlacing}
                variant="primaryButton"
              />
            )}
            
            {placingMode && (
              <CustomButton
                text="Cancel"
                onClick={cancelPlacing}
                variant="dangerButton"
              />
            )}
          </div>
        </div>

        <div className="landmarkManagerContent">
          <div className="mapPanel">
            <MapContainer
              center={defaultCenter}
              zoom={15}
              style={{ height: '600px', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              
              <MapClickHandler />

              {/* Existing landmarks */}
              {filteredLandmarks.map((landmark) => {
                const position = [landmark.location.coordinates[1], landmark.location.coordinates[0]];
                return (
                  <Marker key={landmark._id} position={position}>
                    <Popup>
                      <div className="landmarkPopup">
                        <h4>{getCategoryIcon(landmark.category)} {landmark.name}</h4>
                        <p><strong>Category:</strong> {landmark.category}</p>
                        {landmark.address && <p><strong>Address:</strong> {landmark.address}</p>}
                        {landmark.phone && <p><strong>Phone:</strong> {landmark.phone}</p>}
                        {landmark.hours && <p><strong>Hours:</strong> {landmark.hours}</p>}
                        {landmark.isFeatured && <p className="featured">⭐ Featured</p>}
                        
                        <div className="popupActions">
                          <button onClick={() => editLandmark(landmark)}>Edit</button>
                          <button onClick={() => handleDeleteLandmark(landmark._id)}>Delete</button>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {/* New marker being placed */}
              {markerPosition && (
                <Marker position={markerPosition}>
                  <Popup>
                    <div>
                      <p>New landmark location</p>
                    </div>
                  </Popup>
                </Marker>
              )}
            </MapContainer>

            {placingMode && (
              <div className="placingInstructions">
                <p>📍 Click on the map to place the landmark</p>
              </div>
            )}
          </div>

          {showLandmarkForm && (
            <div className="landmarkFormPanel">
              <h3>{selectedLandmark ? 'Edit Landmark' : 'New Landmark'}</h3>
              
              <div className="formGroup">
                <label>Landmark Name *</label>
                <input
                  type="text"
                  value={landmarkForm.name}
                  onChange={(e) => setLandmarkForm({ ...landmarkForm, name: e.target.value })}
                  placeholder="E.g., Clubhouse"
                />
              </div>

              <div className="formGroup">
                <label>Category</label>
                <select
                  value={landmarkForm.category}
                  onChange={(e) => setLandmarkForm({ ...landmarkForm, category: e.target.value })}
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="formGroup">
                <label>Description</label>
                <textarea
                  value={landmarkForm.description}
                  onChange={(e) => setLandmarkForm({ ...landmarkForm, description: e.target.value })}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>

              <div className="formGroup">
                <label>Address</label>
                <input
                  type="text"
                  value={landmarkForm.address}
                  onChange={(e) => setLandmarkForm({ ...landmarkForm, address: e.target.value })}
                  placeholder="E.g., 123 Main Street"
                />
              </div>

              <div className="formGroup">
                <label>Phone</label>
                <input
                  type="text"
                  value={landmarkForm.phone}
                  onChange={(e) => setLandmarkForm({ ...landmarkForm, phone: e.target.value })}
                  placeholder="E.g., (555) 123-4567"
                />
              </div>

              <div className="formGroup">
                <label>Website</label>
                <input
                  type="text"
                  value={landmarkForm.website}
                  onChange={(e) => setLandmarkForm({ ...landmarkForm, website: e.target.value })}
                  placeholder="E.g., https://example.com"
                />
              </div>

              <div className="formGroup">
                <label>Hours</label>
                <input
                  type="text"
                  value={landmarkForm.hours}
                  onChange={(e) => setLandmarkForm({ ...landmarkForm, hours: e.target.value })}
                  placeholder="E.g., Mon-Fri 9am-5pm"
                />
              </div>

              <div className="formGroup">
                <label>
                  <input
                    type="checkbox"
                    checked={landmarkForm.isNavigable}
                    onChange={(e) => setLandmarkForm({ ...landmarkForm, isNavigable: e.target.checked })}
                  />
                  Can be used as navigation destination
                </label>
              </div>

              <div className="formGroup">
                <label>
                  <input
                    type="checkbox"
                    checked={landmarkForm.isFeatured}
                    onChange={(e) => setLandmarkForm({ ...landmarkForm, isFeatured: e.target.checked })}
                  />
                  Featured landmark (appears first in lists)
                </label>
              </div>

              <div className="formActions">
                <CustomButton
                  text="Save Landmark"
                  onClick={saveLandmark}
                  variant="successButton"
                />
                <CustomButton
                  text="Cancel"
                  onClick={cancelPlacing}
                  variant="secondaryButton"
                />
              </div>
            </div>
          )}

          {selectedLandmark && !showLandmarkForm && (
            <div className="landmarkDetailsPanel">
              <h3>{getCategoryIcon(selectedLandmark.category)} {selectedLandmark.name}</h3>
              <p><strong>Category:</strong> {selectedLandmark.category}</p>
              {selectedLandmark.address && <p><strong>Address:</strong> {selectedLandmark.address}</p>}
              {selectedLandmark.phone && <p><strong>Phone:</strong> {selectedLandmark.phone}</p>}
              {selectedLandmark.website && <p><strong>Website:</strong> <a href={selectedLandmark.website} target="_blank" rel="noopener noreferrer">{selectedLandmark.website}</a></p>}
              {selectedLandmark.hours && <p><strong>Hours:</strong> {selectedLandmark.hours}</p>}
              {selectedLandmark.description && <p><strong>Description:</strong> {selectedLandmark.description}</p>}
              <p><strong>Navigable:</strong> {selectedLandmark.isNavigable ? 'Yes' : 'No'}</p>
              <p><strong>Featured:</strong> {selectedLandmark.isFeatured ? 'Yes' : 'No'}</p>
              
              <div className="detailActions">
                <CustomButton
                  text="Edit"
                  onClick={() => editLandmark(selectedLandmark)}
                  variant="primaryButton"
                />
                <CustomButton
                  text="Delete"
                  onClick={() => handleDeleteLandmark(selectedLandmark._id)}
                  variant="dangerButton"
                />
                <CustomButton
                  text="Close"
                  onClick={() => setSelectedLandmark(null)}
                  variant="secondaryButton"
                />
              </div>
            </div>
          )}
        </div>

        <div className="landmarkStats">
          <h4>Landmarks by Category</h4>
          {CATEGORIES.map(cat => {
            const count = landmarks.filter(l => l.category === cat.value).length;
            if (count === 0) return null;
            return (
              <div key={cat.value} className="statRow">
                <span>{cat.icon} {cat.label}</span>
                <span className="statCount">{count}</span>
              </div>
            );
          })}
          <div className="statRow total">
            <span>Total Landmarks</span>
            <span className="statCount">{landmarks.length}</span>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LandmarkManager;
